import type { AspectRatio } from '@lucimark/shared/database/custom-types/aspect-ratio'
import type { TPortalCpsLoopSegmentSource } from '@lucimark/shared/helpers/marketplaceCpsLoopPlaylist'
import type { TFrame } from '@lucimark/shared/models/playlist'
import { playlistFrameMatchesPortalAspect } from '@lucimark/shared/helpers/portalListingGate'
import { selectEmbeddedFrames } from '@lucimark/shared/helpers/selectEmbeddedFrames'

function lookupChildFrames(
  childFramesByUrn: Readonly<Record<string, readonly TFrame[]>>,
  urn: string
): readonly TFrame[] {
  const direct = childFramesByUrn[urn]
  if (direct != null) {
    return direct
  }
  const key = [...Object.keys(childFramesByUrn)].find(k => k.toLowerCase() === urn.toLowerCase())
  return key != null ? (childFramesByUrn[key] ?? []) : []
}

function isDirectAssetHouseFrame(frame: TFrame): boolean {
  const facet = frame.facets?.main
  return facet != null && frame.duration > 0
}

/** Inner row from an embedded playlist (editorial resolve; e2e may strip facets). */
function isInnerEmbeddedAssetFrame(frame: TFrame): boolean {
  return !frame.playlistRef && frame.duration > 0
}

function hasEligibleInnerAssetFrames(
  childFrames: readonly TFrame[],
  viewportAspect: AspectRatio
): boolean {
  return childFrames.some(
    frame => !frame.playlistRef
      && isDirectAssetHouseFrame(frame)
      && playlistFrameMatchesPortalAspect(frame, viewportAspect)
  )
}

/** True when a nested `playlistRef` row has at least one playable inner asset frame. */
export function hasExpandableNestedPlaylistRefFrame(
  frame: TFrame,
  childFramesByUrn: Readonly<Record<string, readonly TFrame[]>>
): boolean {
  const refUrn = frame.playlistRef?.playlistUrn?.trim()
  if (refUrn == null || refUrn === '') {
    return false
  }
  const childFrames = lookupChildFrames(childFramesByUrn, refUrn)
  return childFrames.some(inner => isInnerEmbeddedAssetFrame(inner))
}

/** Expand a nested `playlistRef` frame into inner asset frames (depth-1 only). */
export function expandNestedPlaylistRefFrame(
  frame: TFrame,
  childFramesByUrn: Readonly<Record<string, readonly TFrame[]>>,
  filterInner?: (frame: TFrame) => boolean
): TFrame[] {
  const ref = frame.playlistRef
  if (ref == null) {
    return [frame]
  }
  const refUrn = ref.playlistUrn.trim()
  const childFrames = lookupChildFrames(childFramesByUrn, refUrn)
  const activeInner = childFrames.filter(
    inner => isInnerEmbeddedAssetFrame(inner)
      && (filterInner == null || filterInner(inner))
  )
  if (activeInner.length === 0) {
    return []
  }
  return selectEmbeddedFrames([...activeInner], ref, frame.id)
}

/**
 * Top-level house playlist frames eligible for CPS rotation (direct assets and nested
 * `playlistRef` slots with at least one playable inner asset).
 */
export function buildPortalCpsHouseEligibleFrames(params: {
  frames: readonly TFrame[]
  viewportAspect: AspectRatio
  childFramesByUrn: Readonly<Record<string, readonly TFrame[]>>
}): TFrame[] {
  const eligible: TFrame[] = []
  for (const frame of params.frames) {
    if (!playlistFrameMatchesPortalAspect(frame, params.viewportAspect)) {
      continue
    }
    if (isDirectAssetHouseFrame(frame)) {
      eligible.push(frame)
      continue
    }
    const refUrn = frame.playlistRef?.playlistUrn?.trim()
    if (refUrn == null || refUrn === '') {
      continue
    }
    const childFrames = lookupChildFrames(params.childFramesByUrn, refUrn)
    if (hasEligibleInnerAssetFrames(childFrames, params.viewportAspect)) {
      eligible.push(frame)
    }
  }
  return eligible
}

export interface TExpandedPortalCpsHousePick {
  segments: TPortalCpsLoopSegmentSource[]
  sourceFrames: TFrame[]
}

/** Expand a picked house frame (direct asset or nested playlist ref) into playable segments. */
export function expandPortalCpsHousePickedFrame(
  frame: TFrame,
  childFramesByUrn: Readonly<Record<string, readonly TFrame[]>>,
  viewportAspect: AspectRatio
): TExpandedPortalCpsHousePick | null {
  const facet = frame.facets?.main
  if (facet != null && frame.duration > 0) {
    return {
      segments: [{
        assetUrn: facet.asset,
        assetType: facet.type,
        durationSeconds: frame.duration
      }],
      sourceFrames: [frame]
    }
  }

  const ref = frame.playlistRef
  if (ref == null) {
    return null
  }
  const selected = expandNestedPlaylistRefFrame(
    frame,
    childFramesByUrn,
    inner => playlistFrameMatchesPortalAspect(inner, viewportAspect)
  )
  const segments: TPortalCpsLoopSegmentSource[] = []
  const sourceFrames: TFrame[] = []
  for (const inner of selected) {
    const innerFacet = inner.facets?.main
    if (innerFacet == null || inner.duration <= 0) {
      continue
    }
    segments.push({
      assetUrn: innerFacet.asset,
      assetType: innerFacet.type,
      durationSeconds: inner.duration
    })
    sourceFrames.push(inner)
  }
  if (segments.length === 0) {
    return null
  }
  return { segments, sourceFrames }
}
